package dev.kuml.portal.web.editor

import io.kvision.html.Div
import kotlinx.browser.document
import org.w3c.dom.Element

private const val SVG_NS = "http://www.w3.org/2000/svg"

private fun createSvgElement(tag: String): Element = document.createElementNS(SVG_NS, tag) as Element

/** R-28's cursor-arrow glyph, in a compact local coordinate box -- see
 * [PresenceOverlayHandle.repaint]'s `kportal-presence-cursor-inner` counter-scale, which keeps this
 * a constant screen size regardless of diagram zoom. */
private const val CURSOR_ARROW_PATH = "M0 0 L0 14 L4 11 L7 17 L9 16 L6 10 L11 10 Z"

/**
 * Converts a client (screen) point into the SAME viewBox-unit coordinate system the inlined diagram
 * `<svg>` (and, sharing its viewBox, the presence overlay `<svg>` [PresenceOverlay.create] builds)
 * both use -- via [root]'s own screen CTM, inverted. [root] is ordinarily `EditorScreen.kt`'s
 * `inlineSvgRoot` (the diagram's own `<svg>` root element), whose CTM already accounts for the
 * ambient `.kportal-zoom-wrapper` CSS transform (translate+scale) AND any browser/OS zoom -- so the
 * math below (`x' = a*x + c*y + e`, `y' = b*x + d*y + f`, the standard 2D affine matrix form) needs
 * no separate knowledge of the current zoom/pan state at all.
 *
 * `getScreenCTM()`/`DOMMatrix.inverse()` are not exercised via typed Kotlin/JS DOM bindings
 * elsewhere in this module (same kind of gap `ShareDialog.kt`'s `.asDynamic()` clipboard call works
 * around for `navigator.clipboard`) -- [runCatching] both covers that gap AND the "[root] isn't
 * rendered yet, `getScreenCTM()` returns `null`/`undefined`" case the R-28 plan calls out as "skip
 * this frame" (calling `.inverse()` on a null/undefined CTM throws in the underlying JS, caught
 * here).
 */
public fun clientPointToViewBox(
    root: Element,
    clientX: Double,
    clientY: Double,
): Pair<Double, Double>? =
    runCatching {
        val inverse = root.asDynamic().getScreenCTM().inverse()
        val a: Double = inverse.a
        val b: Double = inverse.b
        val c: Double = inverse.c
        val d: Double = inverse.d
        val e: Double = inverse.e
        val f: Double = inverse.f
        (a * clientX + c * clientY + e) to (b * clientX + d * clientY + f)
    }.getOrNull()

/**
 * Owns the R-28 presence overlay's live `<svg>` DOM node -- see [PresenceOverlay.create]. Not
 * covered by `jsNodeTest` (no real DOM/SVG geometry in that runtime, same convention `CollabClient
 * .kt`/`SvgInliner.kt` already follow, see this module's `README.adoc`) -- verified manually
 * against the live deployment instead.
 */
public class PresenceOverlayHandle(
    private val svg: Element,
) {
    /**
     * Full repaint on every call (see `DiagramCanvas.applyHighlight`'s identical convention -- a
     * kUML diagram's presence overlay has, at most, a handful of peer cursors and a handful of
     * selection rings, never enough elements for incremental diffing to matter). Clears every
     * previously painted child and rebuilds from [peers]/[ownSelection] fresh -- real SVG element
     * creation (`document.createElementNS`) and real text nodes (`.textContent =`) only, never
     * `innerHTML`, so a peer's display name can never inject markup.
     *
     * @param peers this diagram's current peer-presence map (`EditorScreen.kt`'s `presenceState`).
     * @param identities peer identities keyed by the SAME `tokenHex` [peers] uses -- for
     *   [PeerCursorPalette.forUserId] (colour) and the name shown on each cursor's flag.
     * @param outlineRoot the diagram's own inlined `<svg>` root (`inlineSvgRoot`) -- read for
     *   `getBoundingClientRect()` of each selected `[data-kuml-id]` element, converted into this
     *   overlay's shared viewBox space via [clientPointToViewBox] (never `getBBox()`, which -- per
     *   the SVG spec -- excludes an element's OWN `transform` attribute and would misplace every
     *   ring by that element's translate). Element lookup goes through [DiagramCanvas]'s own
     *   `querySelectorAll("[data-kuml-id]")` + attribute-compare convention (see
     *   [paintSelectionRings], security fix 2026-09-03) rather than interpolating a peer-controlled
     *   id into a CSS attribute selector -- a `PeerPresence.selection` id is only checked against
     *   `knownIds` (see `PresencePayload.decode`), never against a character allowlist, and a kUML
     *   author is free to pick an id containing `"` or `]` (`UmlIds.child`'s "User override" `id =
     *   "…"` parameter is passed through unfiltered, per that function's own KDoc).
     * @param ownSelection this connection's OWN selection -- excluded from the peer selection rings
     *   below (a ring around what you already see highlighted via [DiagramCanvas.applyHighlight]
     *   would be redundant clutter).
     * @param scale the diagram's current zoom (`EditorScreen.kt`'s `scale`) -- neutralised via a
     *   `scale(1/[scale])` transform on each cursor's inner group and on the selection-ring stack
     *   offset, so cursor icons/flags/ring insets stay a constant SCREEN size regardless of zoom
     *   (positions themselves need no such correction: both this `<svg>` and the diagram `<svg>`
     *   are CSS-transformed together as siblings inside the same `.kportal-zoom-wrapper`, see
     *   [PresenceOverlay.create]).
     * @param now `Date.now()` at repaint time -- forwarded to [PresenceState.visibleCursorOf] for
     *   the cursor TTL (a stale cursor is simply omitted; its selection ring, if any, is not).
     */
    public fun repaint(
        peers: Map<String, PeerPresence>,
        identities: Map<String, CollabPeer>,
        outlineRoot: Element,
        ownSelection: SelectionState,
        scale: Double,
        now: Double,
    ) {
        // Defense in depth, not the primary fix (see paintSelectionRings): a single malformed/future
        // DOM failure while painting one peer's presence must never take down the whole overlay for
        // every peer for the rest of the session -- this used to be exactly the failure mode of the
        // 2026-09-03 CSS-selector-injection bug (an uncaught DOMException here skipped paintCursors
        // below AND every subsequent repaint() call, since neither this nor any of its callers --
        // EditorScreen.kt's `presenceTickTimer` interval and its per-frame repaint call -- had a
        // try/catch of their own).
        runCatching {
            while (true) {
                val child = svg.firstChild ?: break
                svg.removeChild(child)
            }
            val inverseScale = if (scale > 0.0) 1.0 / scale else 1.0
            paintSelectionRings(peers, identities, outlineRoot, ownSelection, inverseScale)
            paintCursors(peers, identities, inverseScale, now)
        }
    }

    private fun paintSelectionRings(
        peers: Map<String, PeerPresence>,
        identities: Map<String, CollabPeer>,
        outlineRoot: Element,
        ownSelection: SelectionState,
        inverseScale: Double,
    ) {
        val ownIds = (ownSelection.nodeIds + listOfNotNull(ownSelection.edgeId)).toSet()
        val elementIds = peers.values.flatMapTo(mutableSetOf()) { it.selection }
        if (elementIds.isEmpty()) return
        // Resolve every wanted id via `querySelectorAll("[data-kuml-id]")` + a Kotlin-side attribute
        // compare -- the SAME convention `DiagramCanvas.applyHighlight` already uses -- rather than
        // interpolating a peer-controlled `elementId` into a `querySelector("[data-kuml-id=\"...\"]")`
        // CSS attribute-selector string. `elementId` comes from `PeerPresence.selection`, which
        // `PresencePayload.decode` validates only via `it in knownIds`, never against a character
        // allowlist -- a kUML author's freely chosen classifier name (or an explicit `UmlIds.child`
        // `id = "…"` override, passed through completely unfiltered per its own KDoc) can legally
        // contain `"`, `]`, or other CSS-selector metacharacters. Interpolating such an id straight
        // into a selector string produces a syntactically invalid selector, and `querySelector` throws
        // an uncaught `DOMException: SyntaxError` for that -- which used to abort this whole repaint
        // (see [repaint]'s own KDoc) for every peer, not just the one with the offending selection.
        val targetsById = mutableMapOf<String, Element>()
        val candidates = outlineRoot.querySelectorAll("[data-kuml-id]")
        for (i in 0 until candidates.length) {
            val element = candidates.item(i) as? Element ?: continue
            val id = element.getAttribute("data-kuml-id") ?: continue
            if (id in elementIds && id !in targetsById) targetsById[id] = element
        }
        for (elementId in elementIds) {
            if (elementId in ownIds) continue
            val target = targetsById[elementId] ?: continue
            val rect = target.asDynamic().getBoundingClientRect()
            val left: Double = rect.left
            val top: Double = rect.top
            val right: Double = rect.right
            val bottom: Double = rect.bottom
            val topLeft = clientPointToViewBox(outlineRoot, left, top) ?: continue
            val bottomRight = clientPointToViewBox(outlineRoot, right, bottom) ?: continue
            // At most 3 stacked rings per element (design-fazit) -- beyond that, further peers
            // selecting the same element simply don't get their own ring, avoiding an ever-growing
            // nest of outlines around a popular node.
            PresenceState.peersSelecting(peers, elementId).take(3).forEachIndexed { index, tokenHex ->
                val palette = PeerCursorPalette.forUserId(identities[tokenHex]?.userId ?: tokenHex)
                val inset = index * 3.0 * inverseScale
                val ring = createSvgElement("rect")
                ring.setAttribute("class", "kportal-presence-ring")
                ring.setAttribute("x", (topLeft.first - inset).toString())
                ring.setAttribute("y", (topLeft.second - inset).toString())
                ring.setAttribute("width", (bottomRight.first - topLeft.first + 2 * inset).toString())
                ring.setAttribute("height", (bottomRight.second - topLeft.second + 2 * inset).toString())
                ring.setAttribute("stroke", palette.stroke)
                svg.appendChild(ring)
            }
        }
    }

    private fun paintCursors(
        peers: Map<String, PeerPresence>,
        identities: Map<String, CollabPeer>,
        inverseScale: Double,
        now: Double,
    ) {
        for ((tokenHex, peer) in peers) {
            val cursor = PresenceState.visibleCursorOf(peer, now) ?: continue
            val identity = identities[tokenHex]
            val displayName = identity?.displayName?.ifEmpty { "?" } ?: "?"
            val palette = PeerCursorPalette.forUserId(identity?.userId ?: tokenHex)

            val outer = createSvgElement("g")
            outer.setAttribute("class", "kportal-presence-cursor-outer")
            outer.setAttribute("transform", "translate(${cursor.first}, ${cursor.second})")

            val inner = createSvgElement("g")
            inner.setAttribute("class", "kportal-presence-cursor-inner")
            inner.setAttribute("transform", "scale($inverseScale)")

            val arrow = createSvgElement("path")
            arrow.setAttribute("class", "kportal-presence-cursor-arrow")
            arrow.setAttribute("d", CURSOR_ARROW_PATH)
            arrow.setAttribute("fill", palette.stroke)
            inner.appendChild(arrow)

            val flagWidth = (displayName.length * 6 + 12).coerceAtLeast(24)
            val flag = createSvgElement("g")
            flag.setAttribute("class", "kportal-presence-flag")
            flag.setAttribute("transform", "translate(14, 12)")
            val flagRect = createSvgElement("rect")
            flagRect.setAttribute("width", flagWidth.toString())
            flagRect.setAttribute("height", "18")
            flagRect.setAttribute("fill", palette.flag)
            flag.appendChild(flagRect)
            val text = createSvgElement("text")
            text.setAttribute("x", "6")
            text.setAttribute("y", "13")
            text.textContent = displayName
            flag.appendChild(text)
            inner.appendChild(flag)

            outer.appendChild(inner)
            svg.appendChild(outer)
        }
    }
}

public object PresenceOverlay {
    /**
     * Creates the overlay `<svg>` as a CHILD of [wrapper] (`EditorScreen.kt`'s `zoomWrapper`) -- NOT
     * a sibling `div` (a normal flex sibling of that flex container would sit NEXT TO the diagram,
     * never over it -- `.kportal-zoom-wrapper` is `display:flex` per `app.css`, verified). Sharing
     * [wrapper] means the overlay is scaled/panned in perfect lockstep with the diagram's own
     * `<svg>` -- both are children subject to the exact same CSS `transform` [wrapper] itself
     * carries (see `EditorScreen.kt`'s `applyZoomTransform`) -- so a peer cursor painted at a given
     * viewBox coordinate never drifts from the diagram element it points at, at any zoom level, with
     * no extra transform bookkeeping here. `pointer-events: none` (`app.css`'s
     * `.kportal-presence-overlay`) keeps it purely visual -- clicks/pointer events pass straight
     * through to the diagram `<svg>` underneath.
     */
    public fun create(
        wrapper: Div,
        viewBoxAttr: String,
        widthPx: Double,
        heightPx: Double,
    ): PresenceOverlayHandle {
        val svg = createSvgElement("svg")
        svg.setAttribute("class", "kportal-presence-overlay")
        svg.setAttribute("viewBox", viewBoxAttr)
        svg.setAttribute("width", widthPx.toString())
        svg.setAttribute("height", heightPx.toString())
        wrapper.getElement()?.appendChild(svg)
        return PresenceOverlayHandle(svg)
    }
}
